import type { CartBundleItem, CartItem } from '@/ui/store/cart/types';
import { generateRandomId } from '@/ui/utils/helpers';
import { getCartItemMergeKey } from '@/utils/cart-line-grouping';
import type { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import type { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import type { BundleProduct } from '../BundleProductService';
import type { Bundle } from '../BundleService';

function getSubscription(variant: ParsedProductVariant): CartItem['subscription'] {
  if (!variant.subscriptionPlan) {
    return undefined;
  }

  const { identifier, periods } = variant.subscriptionPlan;
  return { subscriptionPlanId: identifier, period: periods[0] };
}

function createCartItem(variant: ParsedProductVariant, bundleProduct: BundleProduct): CartItem {
  // The backend clamps the bundle discount to the item's base price, so the cart must not
  // promise more than that.
  const discount = Math.min(bundleProduct.discount, bundleProduct.getVariantPrice(variant));
  return {
    id: bundleProduct.id,
    cartItemId: generateRandomId(),
    shape: bundleProduct.shape,
    name: variant.name,
    image: variant.images[0],
    priceVariant: variant.priceVariant,
    discount,
    quantity: 1,
    sku: variant.sku,
    url: bundleProduct.path,
    subscription: getSubscription(variant),
  };
}

// One cart item per distinct merge key; identical slots raise its quantity.
// A slot whose resolved variant can't be bought maps to null, so the caller can refuse the bundle.
export function mapSelectedVariantsToCartItems(
  products: BundleProduct[],
  selected: SelectedProductVariants,
): Array<CartItem | null> {
  const itemsMap = new Map<string, CartItem | null>();

  products.forEach((bundleProduct) => {
    const variant = bundleProduct.resolveVariant(selected);

    if (!variant || (!variant.priceVariant && !variant.subscriptionPlan)) {
      itemsMap.set(`unpurchasable-${bundleProduct.bundleProductInstanceId}`, null);
      return;
    }

    // Keyed like the order lines are merged, so the cart and the order pages group alike.
    const item = createCartItem(variant, bundleProduct);
    const uniqueKey = getCartItemMergeKey(item);
    const existingItem = itemsMap.get(uniqueKey);

    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      itemsMap.set(uniqueKey, item);
    }
  });

  return Array.from(itemsMap.values());
}

export function createCartBundleItem(
  bundle: Bundle,
  variantCartItems: CartItem[],
  selected?: SelectedProductVariants,
): CartBundleItem | null {
  if (!bundle.documentExtRef) {
    // can't add a bundle without external ref to cart
    console.error('Bundle is missing external reference');
    return null;
  }

  return {
    id: bundle.id,
    documentExtRef: bundle.documentExtRef,
    cartBundleId: generateRandomId(),
    name: bundle.name,
    image: bundle.getImagesForSelection(selected)[0] ?? bundle.firstImage,
    productVariants: variantCartItems,
    quantity: 1,
  };
}
